<template>
  <b-card>
    <b-alert
      v-if="showAccountCompletionWelcome"
      show
      variant="primary"
      class="account-completion-welcome mb-2"
      data-testid="account-completion-welcome"
    >
      <p class="mb-0 font-weight-bold">
        {{ $t('approvalLifecycle.accountCompletion.welcome') }}
      </p>
      <p v-if="showAccountCompletionFreeAccessHint" class="mt-50 mb-0">
        {{ $t('approvalLifecycle.accountCompletion.freeAccessHint') }}
      </p>
    </b-alert>
    <account-onboarding-status
      v-if="optionsLocal && !showAccountCompletionWelcome"
      :account="optionsLocal"
      surface="account-settings"
      dismissible
      class="mb-2"
    />
    <b-row>
      <b-col
        v-if="optionsLocal && !optionsLocal.emailApproved && !emailConfirmLiveInputVisible"
        cols="12"
        class="mt-75"
      >
        <!-- PM 06.08.2026 item 1. The variant drives BOTH the text colour and
             the background wash (base/core/colors/_palette.scss), so
             `secondary` rendered grey-on-grey and the copy was barely
             readable. `warning` matches the phone banner below. -->
        <b-alert
          show
          variant="warning"
          class="mb-10"
          data-testid="email-banner"
        >
          <h4
            class="alert-heading"
            style="
              display: flex;
              flex-direction: column;
              flex-wrap: wrap;
              align-content: center;
              align-items: center;
            "
          >
            {{ optionsLocal.email
              ? $t('Verify your recovery email address.')
              : $t('Add a recovery email address (recommended).') }}
            <small class="font-weight-normal text-center mt-50 mb-1">
              {{ $t('A verified recovery email lets you reset your password and receive application notifications.') }}
            </small>
            <b-link
              class="alert-link"
              @click="emailBannerAction"
            >
              <b-button variant="info">
                {{ optionsLocal.email ? $t('Verify email address') : $t('Add an email address') }}
              </b-button>
            </b-link>
          </h4>
        </b-alert>
      </b-col>
      <b-col
        v-if="optionsLocal && !optionsLocal.phoneApproved"
        cols="12"
        class="mt-75"
      >
        <b-alert
          show
          variant="warning"
          class="mb-10"
          data-testid="phone-banner"
        >
          <h4
            class="alert-heading"
            style="
              display: flex;
              flex-direction: column;
              flex-wrap: wrap;
              align-content: center;
              align-items: center;
            "
          >
            {{ optionsLocal.phone ? $t('Your phone is not confirmed.') : $t('You have not added a phone number yet.') }}
            <br>
            <br>
            <b-link
              class="alert-link"
              @click="phoneBannerAction"
            >
              <b-button variant="info">
                {{ optionsLocal.phone ? $t('Verify phone') : $t('Add a phone number') }}
              </b-button>
            </b-link>
          </h4>
        </b-alert>
      </b-col>
    </b-row>
    <!-- media -->
    <b-media
      no-body
      class="mb-1"
    >
      <b-media-aside>
        <b-link>
          <b-img
            ref="previewEl"
            rounded
            :src="
              optionsLocal
                ? optionsLocal.avatar
                  ? optionsLocal.avatar
                  : baseURL + '/images/dikologo.png'
                : baseURL + '/images/dikologo.png'
            "
            height="80"
          />
        </b-link>
        <!--/ avatar -->
      </b-media-aside>
      <b-media-body class="mt-75 ml-75">
        <!-- upload button -->
        <b-card-text style="margin-bottom: 3px; margin-top: -8px">{{ $t("Panel Image")
        }}</b-card-text>
        <b-button
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          variant="primary"
          size="sm"
          class="mb-75 mr-75"
          :disabled="profileSaving"
          @click="fileinputclick"
        >
          {{ $t("Upload") }}
        </b-button>
        <!--/ upload button -->
        <!-- reset -->
        <b-button
          v-ripple.400="'rgba(186, 191, 199, 0.15)'"
          variant="outline-secondary"
          size="sm"
          class="mb-75 mr-75"
          :disabled="profileSaving"
          @click="resetImage"
        >
          {{ $t("Revert") }}
        </b-button>
        <!--/ reset -->
        <b-card-text style="margin-top: -3px">{{ $t("Allowed account image formats and size")
        }}</b-card-text>
      </b-media-body>
    </b-media>
    <!--/ media -->
    <!-- form -->
    <b-form
      v-if="optionsLocal"
      ref="editProfileGeneralForm"
      class="mt-2 mb-1"
      autocomplete="off"
      :aria-busy="profileSaving ? 'true' : 'false'"
      @submit.prevent
    >
      <b-form-file
        id="fileinput"
        ref="refInputEl"
        v-model="profileFile"
        accept=".jpg, .jpeg, .png, .gif"
        :disabled="profileSaving"
        :hidden="true"
        plain
        @input="inputImageRenderer"
      />
      <b-row>
        <!-- <b-col sm="6">
          <b-form-group
            label="Username"
            label-for="account-username"
          >
            <b-form-input
              v-model="optionsLocal.username"
              placeholder="Username"
              name="username"
            />
          </b-form-group>
        </b-col> -->
        <!-- <b-col sm="12">
          <b-form-group label-for="account-company">
            <b-form-input
              v-model="optionsLocal.company"
              name="company"
              placeholder="Company name"
            />
            <h3>{{ optionsLocal.company }}</h3>
          </b-form-group>
        </b-col> -->
        <b-col
          sm="6"
          class="mb-1"
        >
          <b-form-group
            :label="$t('Full Name')"
            label-for="account-name"
            :class="completionFieldClasses.name"
            :data-completion-state="completionFieldStateLabels.name"
            data-testid="account-name-completion"
          >
            <b-form-input
              v-model="optionsLocal.name"
              :disabled="profileSaving"
              name="name"
              :placeholder="$t('Full Name')"
              autocomplete="off"
              :formatter="trTitleCase"
            />
          </b-form-group>
        </b-col>
        <b-col
          sm="6"
          class="mb-1"
        >
          <b-form-group
            :label="$t('Surname')"
            label-for="account-surname"
            :class="completionFieldClasses.surname"
            :data-completion-state="completionFieldStateLabels.surname"
            data-testid="account-surname-completion"
          >
            <b-form-input
              v-model="optionsLocal.surname"
              :disabled="profileSaving"
              name="surname"
              :placeholder="$t('Surname')"
              autocomplete="off"
              :formatter="trTitleCase"
            />
          </b-form-group>
        </b-col>
        <b-col
          sm="6"
          class="mb-1"
        >
          <b-form-group
            :label="$t('Email')"
            label-for="account-e-mail"
            :class="completionFieldClasses.email"
            :data-completion-state="completionFieldStateLabels.email"
            data-testid="account-email-completion"
          >
            <b-input-group>
              <!-- Gated: readonly until the change button opens an explicit
                   edit; a freely editable field invited "silent" edits that
                   never went through the password+OTP lane. -->
              <b-form-input
                v-model="optionsLocal.email"
                name="email"
                :placeholder="$t('E-mail')"
                autocomplete="off"
                :disabled="!emailEditing"
                :formatter="emailFormatter"
                type="email"
                :state="!optionsLocal.email ? null : emailInputValid"
                @blur="emailValidate($event)"
                @focus="
                  $event.target.classList.remove('is-invalid');
                  $event.target.placeholder = $t('E-mail');
                "
              />
              <b-input-group-append>
                <!-- Item 2: "not verified yet" is an outstanding action, not
                     an error, so it no longer uses the danger variant. -->
                <b-button
                  data-testid="email-edit-toggle"
                  :variant="emailEditing && emailChangeOpen
                    ? 'outline-secondary'
                    : (optionsLocal.emailApproved ? 'success' : 'outline-primary')"
                  @click="emailEditing && emailChangeOpen ? cancelEmailChange() : openEmailVerification()"
                >
                  <feather-icon
                    v-if="!(emailEditing && emailChangeOpen)"
                    :icon="optionsLocal.emailApproved ? 'CheckIcon' : 'AlertCircleIcon'"
                    size="14"
                  />
                  {{ emailEditing && emailChangeOpen
                    ? $t('Cancel')
                    : (optionsLocal.email ? $t('Update email address') : $t('Add an email address')) }}
                </b-button>
              </b-input-group-append>
            </b-input-group>
            <small
              v-if="optionsLocal.email && !emailInputValid"
              class="text-danger d-block"
            >{{ $t('Invalid Email') }}</small>
          </b-form-group>
        </b-col>
        <!-- Item 3: the phone sits beside the e-mail so the two contact
             fields share one row, and the conditional verification panels
             below no longer split them. -->
        <b-col
          sm="6"
          class="mb-1"
        >
          <b-form-group
            :label="$t('Phone Number')"
            label-for="account-phone"
            :class="completionFieldClasses.phone"
            :data-completion-state="completionFieldStateLabels.phone"
            data-testid="account-phone-completion"
          >
            <b-input-group>
              <!-- Gated like the email field. The disabled state also kills
                   the incident where a mere focus+blur re-ran the formatter,
                   mutated the bound value to "(5xx) xxx xx xx" and made the
                   verified badge read the phone as changed. -->
              <b-form-input
                v-model="optionsLocal.phone"
                name="phone"
                :placeholder="$t('Phone Number')"
                autocomplete="off"
                :disabled="!phoneEditing"
                :formatter="phoneFormatter"
                :state="!optionsLocal.phone ? null : phoneInputValid"
                @focus="$event.target.placeholder = $t('Phone Number')"
              />
              <b-input-group-append>
                <b-button
                  v-if="phoneEditing"
                  data-testid="phone-verify-button"
                  variant="primary"
                  :disabled="!phoneInputValid"
                  @click="openPhoneVerification"
                >
                  {{ $t('Verify phone') }}
                </b-button>
                <!-- Item 2: "not verified yet" is an outstanding action, not
                     an error, so it no longer uses the danger variant. -->
                <b-button
                  data-testid="phone-edit-toggle"
                  :variant="phoneEditing
                    ? 'outline-secondary'
                    : (phoneBadgeVerified ? 'success' : 'outline-primary')"
                  @click="phoneEditing ? cancelPhoneEdit() : startPhoneEdit()"
                >
                  <feather-icon
                    v-if="!phoneEditing"
                    :icon="phoneBadgeVerified ? 'CheckIcon' : 'AlertCircleIcon'"
                    size="14"
                  />
                  {{ phoneEditing
                    ? $t('Cancel')
                    : (phoneBadgeVerified ? $t('Update phone number') : $t('Verify phone')) }}
                </b-button>
              </b-input-group-append>
            </b-input-group>
            <small
              v-if="optionsLocal.phone && !phoneInputValid"
              class="text-danger d-block"
            >{{ $t('Enter a valid mobile phone number') }}</small>
            <small
              v-if="!phoneBadgeVerified"
              class="text-warning"
            >{{ optionsLocal.phone ? $t('Your phone is not confirmed.') : $t('Add a phone number') }}</small>
          </b-form-group>
        </b-col>
        <b-col
          v-if="emailConfirmOpen"
          cols="12"
          class="mb-1"
          data-testid="email-confirm-section"
        >
          <!-- WP2: two states, and the difference is the whole point of this
               section. With a LIVE challenge (adopted from the active lookup,
               or just minted by an explicit send) the code input is shown so
               the user can type the code already sitting in their inbox.
               Without one, only an explicit send action is offered -- opening
               this section must never mint a code behind the user's back,
               because minting supersedes the code they are about to type. -->
          <template v-if="emailConfirmChallengeId">
            <b-card-text>{{ $t('Enter the six-digit code sent to your email.') }}</b-card-text>
            <b-form-group
              :label="$t('Verification Code')"
              label-for="account-email-confirm-code"
            >
              <b-form-input
                id="account-email-confirm-code"
                v-model="emailConfirmCode"
                class="verification-code-input"
                data-testid="email-confirm-code-input"
                maxlength="6"
                inputmode="numeric"
                autocomplete="one-time-code"
                :formatter="codeFormatter"
                @keyup.enter="verifyEmailConfirm"
              />
            </b-form-group>
            <small
              v-if="emailConfirmExpired"
              class="text-danger d-block"
              data-testid="email-confirm-expired"
            >{{ $t('The code has expired. Request a new one.') }}</small>
            <small
              v-else-if="emailConfirmExpiresIn > 0"
              class="text-muted d-block"
              data-testid="email-confirm-expiry"
            >{{ $t('The code expires in {time}.', { time: emailConfirmExpiryDisplay }) }}</small>
            <small
              v-if="emailConfirmError"
              class="text-danger d-block"
            >{{ emailConfirmError }}</small>
            <b-button
              variant="primary"
              class="mt-1 mr-1"
              data-testid="email-confirm-verify"
              :disabled="emailConfirmLoading || emailConfirmExpired || !/^\d{6}$/.test(emailConfirmCode)"
              @click="verifyEmailConfirm"
            >
              {{ $t('Verify') }}
            </b-button>
            <b-button
              variant="outline-secondary"
              class="mt-1 mr-1"
              data-testid="email-confirm-resend"
              :disabled="emailConfirmRequesting"
              @click="requestEmailConfirmCode"
            >
              {{ $t('Resend Code') }}
            </b-button>
            <b-button
              variant="outline-secondary"
              class="mt-1"
              @click="cancelEmailConfirm"
            >
              {{ $t('Cancel') }}
            </b-button>
            <small
              class="text-muted d-block mt-50"
              data-testid="email-confirm-resend-warning"
            >{{ $t('Sending a new code makes the code already in your inbox stop working.') }}</small>
          </template>
          <template v-else>
            <b-card-text data-testid="email-confirm-no-code">
              {{ $t('No verification code is waiting. Send one to your email address to continue.') }}
            </b-card-text>
            <small
              v-if="emailConfirmError"
              class="text-danger d-block"
            >{{ emailConfirmError }}</small>
            <b-button
              variant="primary"
              class="mt-1 mr-1"
              data-testid="email-confirm-send"
              :disabled="emailConfirmRequesting"
              @click="requestEmailConfirmCode"
            >
              {{ $t('Send Verification Code') }}
            </b-button>
            <b-button
              variant="outline-secondary"
              class="mt-1"
              @click="cancelEmailConfirm"
            >
              {{ $t('Cancel') }}
            </b-button>
          </template>
        </b-col>
        <b-col
          v-if="emailChangeOpen"
          cols="12"
          class="mb-1"
        >
          <b-card-text>{{ emailChangeStep === 'password'
            ? $t('Enter your account password to confirm this change.')
            : $t('Enter the six-digit code sent to your email.') }}</b-card-text>
          <b-form-group
            v-if="emailChangeStep === 'password'"
            :label="$t('Password')"
            label-for="account-email-change-password"
          >
            <b-form-input
              id="account-email-change-password"
              v-model="emailChangePassword"
              type="password"
              autocomplete="current-password"
              @keyup.enter="submitEmailPassword"
            />
          </b-form-group>
          <b-form-group
            v-else
            :label="$t('Verification Code')"
            label-for="account-email-change-code"
          >
            <b-form-input
              id="account-email-change-code"
              v-model="emailChangeCode"
              class="verification-code-input"
              maxlength="6"
              inputmode="numeric"
              autocomplete="one-time-code"
              :formatter="codeFormatter"
              @keyup.enter="verifyEmailChange"
            />
          </b-form-group>
          <small
            v-if="emailChangeError"
            class="text-danger d-block"
          >{{ emailChangeError }}</small>
          <b-button
            variant="primary"
            class="mt-1 mr-1"
            :disabled="emailChangeLoading || (emailChangeStep === 'password' ? (!emailChangePassword || !emailInputValid) : !/^\d{6}$/.test(emailChangeCode))"
            @click="emailChangeStep === 'password' ? submitEmailPassword() : verifyEmailChange()"
          >
            {{ emailChangeStep === 'password' ? $t('Send Verification Code') : $t('Verify') }}
          </b-button>
          <b-button
            variant="outline-secondary"
            class="mt-1"
            @click="cancelEmailChange"
          >
            {{ $t('Cancel') }}
          </b-button>
        </b-col>
        <b-col
          v-if="!optionsLocal.approved && !optionsLocal.awaitingApproval"
          sm="12"
          class="form-group"
        >
          <b-row style="align-items: center">
            <b-col
              sm="6"
              :class="completionFieldClasses.identification"
              :data-completion-state="completionFieldStateLabels.identification"
              data-testid="account-identification-completion"
            >
              <!-- <b-form-group
                :label="$t('Identification Number')"
                label-for="account-identification"
              > -->
              <b-row
                style="
                  padding-left: 1rem;
                  padding-right: 1rem;
                  justify-content: space-between;
                "
              >
                <label>{{ $t("Identification Number") }}</label>
                <b-badge
                  v-b-tooltip.hover.v-primary
                  variant="primary"
                  pill
                  style="cursor: pointer"
                  :title="$t('Your identification number is required to review your application and issue invoices.')"
                > ? </b-badge>
              </b-row>
              <!-- Item 1: an EMPTY field is incomplete, not invalid. The old
                   inline handler fired on `length < 10`, which includes 0, so
                   simply tabbing through painted it red -- and `is-invalid`
                   also suppresses the brand completion emphasis, which
                   targets `.form-control:not(.is-invalid)`. It additionally
                   wiped whatever had been typed. Danger is now reserved for
                   content that is genuinely wrong, and nothing is erased. -->
              <b-form-input
                v-model="optionsLocal.identificationNumber"
                :disabled="profileSaving"
                name="identification"
                required
                :formatter="identificationFormatter"
                :placeholder="identificationPlaceholder"
                :state="identificationInputState"
                @blur="identificationTouched = true"
                @focus="identificationTouched = false"
              />
              <!-- </b-form-group> -->
            </b-col>
            <b-col
              v-if="!optionsLocal.awaitingApproval && !optionsLocal.approved"
              sm="6"
              :class="completionFieldClasses.degree"
              :data-completion-state="completionFieldStateLabels.degree"
              data-testid="account-degree-completion"
            >
              <!-- add file input for degree -->
              <!-- <b-form-group :label="$t('Degree')" label-for="account-degree"> -->
              <b-row
                style="
                  padding-left: 1rem;
                  padding-right: 1rem;
                  justify-content: space-between;
                "
              >
                <label>{{ $t("Degree Or Equivalent Visual") }}</label>
                <b-badge
                  v-b-tooltip.hover.v-primary
                  variant="primary"
                  pill
                  style="cursor: pointer"
                  :title="
                    $t(
                      'This document is required to review your eligibility to use DIKO programs.'
                    )
                  "
                > ? </b-badge>
              </b-row>
              <b-form-file
                id="degreeFileInput"
                ref="degreeFileInput"
                v-model="degreeFile"
                accept=".jpg, .png, .pdf, .jpeg"
                :disabled="profileSaving"
                :placeholder="$t('Degree')"
                :browse-text="$t('Upload')"
              />
              <!-- </b-form-group> -->
            </b-col>
          </b-row>
        </b-col>
        <b-col
          v-if="
            (optionsLocal.identification && optionsLocal.awaitingApproval) ||
              optionsLocal.approved
          "
          sm="6"
          class="mb-1"
        >
          <h3>{{ $t("Identification Number") }}</h3>
          <p>{{ obscureID(optionsLocal.identification) }}</p>
        </b-col>
        <b-col
          v-if="optionsLocal.approved"
          sm="6"
          class="mb-1 d-flex justify-content-around"
        >
          <!-- <b-row>
            <b-col cols="6" class="d-flex align-items-center flex-column">
              <span>{{ $t("Your Referral Code") }}</span>
              <b-button v-b-tooltip.hover.v-primary :title="$t('Share')" variant="primary" pill
                @click="shareReferralCode">
                <FeatherIcon icon="ShareIcon" size="14" />
              </b-button>
            </b-col>
            <b-col cols="6" class="d-flex align-items-center flex-column">
              <span>{{ optionsLocal._id }}</span>
              <b-button variant="primary" pill @click="copyReferralCode" v-b-tooltip.hover.v-primary :title="$t('Copy')">
                <FeatherIcon icon="CopyIcon" size="14" />
              </b-button>
            </b-col>
          </b-row> -->
        </b-col>
        <!-- <b-col sm="12" v-if="!optionsLocal.approved" class="form-group"> -->
        <!-- <b-row style="align-items: center"> -->
        <!-- </b-col> -->
        <!-- </b-row> -->
        <!-- <b-badge v-else class="btn btn-success">
            {{ $t("Approved") }}
          </b-badge> -->
        <!--/ alert -->
        <b-col
          cols="12"
          class="mb-1"
        >
          <b-alert
            v-if="profileSaveError"
            show
            variant="danger"
            class="mt-2 mb-1"
            data-testid="profile-save-error"
          >
            <p class="mb-0">
              {{ $t(profileSaveError) }}
            </p>
            <p
              v-if="profileSaveNeedsCheck"
              class="mt-1 mb-0"
            >{{ $t('accountProfileSave.checkBeforeRetry') }}</p>
            <b-button
              v-if="profileSaveNeedsCheck"
              type="button"
              variant="outline-primary"
              class="mt-1"
              :disabled="profileSaving"
              data-testid="profile-check-status"
              @click="checkProfileSubmission"
            >{{ $t(profileSaving ? 'accountProfileSave.checking' : 'accountProfileSave.checkStatus') }}</b-button>
          </b-alert>
          <b-alert
            v-if="profileSaveNotice"
            show
            variant="success"
            class="mt-2 mb-1"
            data-testid="profile-save-notice"
          >{{ $t(profileSaveNotice) }}</b-alert>
          <b-alert
            v-if="profileRecoveryNotice"
            show
            variant="info"
            class="mt-2 mb-1"
            data-testid="profile-recovery-notice"
          >{{ $t(profileRecoveryNotice) }}</b-alert>
          <b-alert
            v-if="profileRefreshWarning"
            show
            variant="warning"
            class="mt-1 mb-1"
            data-testid="profile-refresh-warning"
          >{{ $t('accountProfileSave.refreshFailed') }}</b-alert>
          <small
            v-if="profileValidationMessage && !optionsLocal.awaitingApproval"
            class="d-block text-danger mt-1"
            data-testid="profile-validation-message"
          >{{ $t(profileValidationMessage) }}</small>
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mt-2 mr-1"
            data-testid="submit-approval-button"
            type="button"
            :disabled="profileSaveDisabled"
            :title="
              optionsLocal.awaitingApproval
                ? $t('Cannot Modify While Waiting Approval')
                : approvalSubmitLabel
            "
            @click="updateProfile"
          >
            <b-spinner
              v-if="profileSaving"
              small
              class="mr-50"
              aria-hidden="true"
            />
            <span aria-live="polite">{{ profileSaving
              ? $t(profileSaveNeedsCheck ? 'accountProfileSave.checking' : 'accountProfileSave.sending')
              : approvalSubmitLabel }}</span>
          </b-button>
          <!-- <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            type="reset"
            class="mt-2"
            @click.prevent="resetForm"
          >
            {{ $t("Reset") }}
          </b-button> -->
        </b-col>
      </b-row>
    </b-form>
    <b-modal
      v-if="optionsLocal"
      ref="sharePlatformsModal"
      hide-footer
      :title="$t('Share On Social Media')"
    >
      <b-row style="justify-content: space-evenly">
        <ShareNetwork
          network="facebook"
          :url="`https://app.dikoturkiye.com/register?referrer=${optionsLocal._id}`"
          :media="`https://app.dikoturkiye.com/${shareImg}`"
          title="Diko Dikkat ve Konsantrasyon Programını Ücretsiz Deneyin!"
          hashtags="diko,dikotr"
        ><b-button variant="facebook">
          <feather-icon
            size="24"
            icon="FacebookIcon"
          />
        </b-button>
        </ShareNetwork>
        <ShareNetwork
          network="whatsapp"
          :url="`https://app.dikoturkiye.com/register?referrer=${optionsLocal._id}`"
          :media="`https://app.dikoturkiye.com/${shareImg}`"
          title="Diko Dikkat ve Konsantrasyon Programını Ücretsiz Deneyin!"
          hashtags="diko,dikotr"
        ><b-button variant="success">
          <b-img :src="whatsappLogo" />
        </b-button></ShareNetwork>
        <ShareNetwork
          network="twitter"
          :url="`https://app.dikoturkiye.com/register?referrer=${optionsLocal._id}`"
          :media="`https://app.dikoturkiye.com/${shareImg}`"
          title="Diko Dikkat ve Konsantrasyon Programını Ücretsiz Deneyin!"
          hashtags="diko,dikotr"
        ><b-button variant="twitter">
          <feather-icon
            icon="TwitterIcon"
            size="24"
          />
        </b-button>
        </ShareNetwork>
      </b-row>
      <b-row style="margin-top: 10px">
        <a
          :href="`https://app.dikoturkiye.com/register?referrer=${optionsLocal._id}`"
        >https://app.dikoturkiye.com/register?referrer={{ optionsLocal._id }}</a>
      </b-row>
    </b-modal>
    <phone-otp-modal
      v-if="phoneOtpOpen"
      :phone="optionsLocal.phone || ''"
      purpose="change"
      @verified="phoneVerified"
      @cancelled="phoneVerificationCancelled"
    />
  </b-card>
</template>

<script>
import {
  BFormFile,
  BBadge,
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BRow,
  BCol,
  BAlert,
  BCard,
  BCardText,
  BMedia,
  BMediaAside,
  BMediaBody,
  BLink,
  BImg,
  VBTooltip,
  BInputGroup,
  BInputGroupAppend,
  BSpinner,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { useInputImageRenderer } from '@core/comp-functions/forms/form-utils'
import { ref } from '@vue/composition-api'
import axios from '@axios'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import shareImg from '@/assets/share.jpg'
import whatsappLogo from '@/assets/walogo.svg'
import { trTitleCase } from '@/libs/textFormat'
import PhoneOtpModal from '@/components/PhoneOtpModal.vue'
import AccountOnboardingStatus from '@/components/AccountOnboardingStatus.vue'
import { freeAccessOf } from '@/libs/freeAccess'
import {
  mergeApprovalLifecycleAccount,
  resolveApprovalLifecycle,
} from '@/libs/approvalLifecycle'
import {
  fetchSubscriptionStanding,
  invalidateSubscriptionStanding,
} from '@/libs/subscriptionStanding'
import { formatPhone, isValidPhone, stripPhone } from '@/libs/phone'
import { isValidEmailStrict } from '@/libs/email'
import { OTP_ERROR_KEYS, OTP_ERROR_KEYS_BY_CODE } from '@/libs/otpI18nKeys'
import {
  PROFILE_UPLOAD_TIMEOUT_MS,
  PROFILE_STATUS_TIMEOUT_MS,
  mergeProfileStatus,
  degreeFileIsValid,
  profileValidationError,
  profileSaveErrorKey,
  profileSaveOutcomeUncertain,
  logProfileSaveFailure,
} from '@/libs/accountProfileSave'

const {
  axiosRoutes: { user: path, auth: authRoutes },
} = require('../../../../G_CONFIG')

export default {
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  components: {
    BButton,
    BForm,
    BImg,
    BFormFile,
    BFormGroup,
    BFormInput,
    BRow,
    BCol,
    BAlert,
    BBadge,
    BCard,
    BCardText,
    BMedia,
    BMediaAside,
    BMediaBody,
    BLink,
    FeatherIcon,
    BInputGroup,
    BInputGroupAppend,
    BSpinner,
    PhoneOtpModal,
    AccountOnboardingStatus,
  },

  // props: {
  //   generalData: {
  //     type: Object,
  //     default: () => {},
  //   },
  // },
  data() {
    return {
      profileFile: null,
      userData: {},
      degreeFile: null,
      profileSaving: false,
      profileSaveDisposed: false,
      profileSaveError: '',
      profileSaveNotice: '',
      profileRecoveryNotice: '',
      profileRefreshWarning: false,
      profileSaveNeedsCheck: false,
      resetUserImage: false,
      shareImg,
      whatsappLogo,
      phoneOtpOpen: false,
      phoneAuthoritative: undefined,
      // Gated-field edit switches: the email/phone inputs stay disabled until
      // their change buttons explicitly open an edit, and cancel restores the
      // authoritative value.
      phoneEditing: false,
      emailEditing: false,
      emailChangeOpen: false,
      emailChangeStep: 'password',
      emailChangePassword: '',
      emailChangeCode: '',
      emailChangeChallengeId: '',
      emailChangeError: '',
      emailChangeLoading: false,
      emailAuthoritative: undefined,
      // WP1: confirm flow -- no password, targets the stored unverified
      // email only. Kept fully separate from the emailChange* state above,
      // which is the existing password-gated change-to-a-new-address flow.
      emailConfirmOpen: false,
      emailConfirmCode: '',
      emailConfirmChallengeId: '',
      emailConfirmError: '',
      emailConfirmLoading: false,
      emailConfirmRequesting: false,
      // WP2: the register mail already carries a live code, and requesting a
      // new one supersedes it. These back the read-only active-challenge
      // lookup that lets the page ADOPT that code instead of voiding it.
      emailConfirmLookupDone: false,
      emailConfirmExpiresIn: 0,
      emailConfirmExpired: false,
      emailConfirmExpiryTimer: null,
      emailConfirmTorndown: false,
      // Item 1: the identification field reports "invalid" only once the user
      // has left it AND left something wrong behind. An untouched or empty
      // field stays in the brand incomplete state instead of turning red.
      identificationTouched: false,
      // optionsLocal: null /* JSON.parse(JSON.stringify(this.generalData)), */,
    }
  },
  computed: {
    profileValidationMessage() {
      return profileValidationError(this.optionsLocal, this.degreeFile, this.profileFile)
    },
    profileSaveDisabled() {
      return !this.optionsLocal || this.profileSaving || this.profileSaveNeedsCheck
        || this.optionsLocal.awaitingApproval || Boolean(this.profileValidationMessage)
        || (this.optionsLocal.approved && !this.isDirty)
    },
    identificationDigits() {
      if (!this.optionsLocal) return ''
      return String(this.optionsLocal.identificationNumber || '')
    },
    // `null` renders neither the valid nor the invalid style, which is what
    // lets the completion-field emphasis show through for an empty field.
    identificationInputState() {
      if (!this.identificationTouched || !this.identificationDigits) return null
      return /^\d{10,11}$/.test(this.identificationDigits) ? null : false
    },
    identificationPlaceholder() {
      return this.identificationInputState === false
        ? this.$t('Minimum 10 digits')
        : this.$t('Identification Number')
    },
    showAccountCompletionWelcome() {
      if (!this.optionsLocal) return false
      return resolveApprovalLifecycle(this.optionsLocal, { surface: 'account-settings' }).state
        === 'not-submitted'
    },
    showAccountCompletionFreeAccessHint() {
      const freeAccess = freeAccessOf(this.optionsLocal)
      return freeAccess.readinessKnown && freeAccess.eligible && freeAccess.state === 'none'
    },
    completionFieldStates() {
      if (!this.optionsLocal) {
        return {
          name: false,
          surname: false,
          email: false,
          phone: false,
          identification: false,
          degree: false,
        }
      }
      return {
        name: Boolean(String(this.optionsLocal.name || '').trim()),
        surname: Boolean(String(this.optionsLocal.surname || '').trim()),
        // Item 2 (PM 05.08.2026): the e-mail is captured during registration,
        // so an unconfirmed address is not an unfilled field. Confirmation is
        // still signalled -- by the adjacent button's icon and variant, and by
        // the recovery-e-mail banner above -- but it no longer paints the field
        // with the "you must fill this in" emphasis.
        email: Boolean(String(this.optionsLocal.email || '').trim()) && this.emailInputValid,
        phone: this.phoneInputValid && this.phoneBadgeVerified,
        identification: /^\d{10,11}$/.test(String(this.optionsLocal.identificationNumber || '')),
        degree: degreeFileIsValid(this.degreeFile),
      }
    },
    completionFieldStateLabels() {
      return Object.keys(this.completionFieldStates).reduce((labels, field) => ({
        ...labels,
        [field]: this.completionFieldStates[field] ? 'complete' : 'incomplete',
      }), {})
    },
    completionFieldClasses() {
      return Object.keys(this.completionFieldStates).reduce((classes, field) => ({
        ...classes,
        [field]: [
          'completion-field',
          this.completionFieldStates[field]
            ? 'completion-field--complete'
            : 'completion-field--incomplete',
        ],
      }), {})
    },
    approvalSubmitLabel() {
      if (!this.optionsLocal) return ''
      if (this.optionsLocal.approved) return this.$t('Save')
      const { approvalStatus } = resolveApprovalLifecycle(this.optionsLocal)
      if (approvalStatus === 'rejected') return this.$t('approvalLifecycle.rejected.cta')
      if (approvalStatus === 'pending') return this.$t('approvalLifecycle.submitted.pendingCta')
      return this.$t('approvalLifecycle.notSubmitted.cta')
    },
    // Dirty flag: Save only lights up once something actually changed.
    isDirty() {
      if (!this.optionsLocal || !this.initialData) return false
      const current = JSON.stringify({
        name: this.optionsLocal.name,
        surname: this.optionsLocal.surname,
        referrer: this.optionsLocal.referrer,
        identificationNumber: this.optionsLocal.identificationNumber,
      })
      return (
        current !== this.initialData
        || !!this.profileFile
        || !!this.degreeFile
        || this.resetUserImage
      )
    },
    phoneInputValid() {
      return isValidPhone(this.optionsLocal.phone || '')
    },
    // CODE-R1-014: phoneApproved alone is stale once the user edits the
    // field to a different (unverified) number — the badge must also
    // require equality with the last authoritative verified phone.
    // Compared CANONICALLY (stripPhone): the b-form-input formatter rewrites
    // the bound value into display shape "(531) 945 00 86" on blur, so a raw
    // string compare read an untouched number as changed and flipped the UI
    // to "not confirmed" (production incident, 2026-07-27).
    phoneBadgeVerified() {
      return !!this.optionsLocal.phoneApproved
        && stripPhone(this.optionsLocal.phone || '') === stripPhone(this.phoneAuthoritative || '')
    },
    // CODE-R1-014: email is no longer PATCHed through the general profile
    // save (the server rejects it there — see updateProfile); it moves
    // through its own password + OTP lane, mirroring phone.
    // Strict ASCII matcher shared with the server's isValidEmailSyntax.
    emailInputValid() {
      return isValidEmailStrict(this.optionsLocal.email || '')
    },
    // The registration-lane confirm code now lives for a full hour, so a raw
    // seconds countdown would read "3600 seconds". Render mm:ss (59:32) and
    // grow to h:mm:ss (1:00:00) whenever an hour-scale TTL is adopted.
    emailConfirmExpiryDisplay() {
      const total = Math.max(0, Math.floor(Number(this.emailConfirmExpiresIn) || 0))
      const hours = Math.floor(total / 3600)
      const minutes = Math.floor((total % 3600) / 60)
      const seconds = total % 60
      const pad = value => String(value).padStart(2, '0')
      return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`
    },
    // The confirm panel showing an input for a LIVE challenge makes the
    // banner's verify action redundant noise, so the banner hides behind
    // this flag. It must flip back the moment the held code dies (expiry)
    // or the panel closes, so the banner's re-entry point never vanishes
    // while there is nothing usable to type.
    emailConfirmLiveInputVisible() {
      return this.emailConfirmOpen && !!this.emailConfirmChallengeId && !this.emailConfirmExpired
    },
  },
  watch: {
    // `immediate` matters: optionsLocal is filled by the setup() axios.get,
    // which can resolve either before this watcher exists (synchronously, as
    // in tests) or long after it (a real network round trip). Firing once up
    // front and again on assignment covers both without double work --
    // lookupActiveEmailConfirm is itself idempotent.
    optionsLocal: {
      immediate: true,
      handler(value) {
        if (value && this.phoneAuthoritative === undefined) this.phoneAuthoritative = value.phone || null
        this.lookupActiveEmailConfirm()
      },
    },
  },
  created() {},
  beforeDestroy() {
    this.profileSaveDisposed = true
    this.emailConfirmTorndown = true
    this.stopEmailConfirmExpiry()
  },
  methods: {
    trTitleCase,
    /* eslint-disable */
    phoneFormatter: formatPhone,
    openPhoneVerification() {
      if (!this.phoneInputValid) return;
      if (this.phoneAuthoritative === undefined) this.phoneAuthoritative = this.optionsLocal.phone || null;
      this.phoneOtpOpen = true;
    },
    phoneVerified(result) {
      this.$set(this.optionsLocal, "phone", result.phone);
      this.$set(this.optionsLocal, "phoneApproved", true);
      this.phoneAuthoritative = result.phone;
      this.phoneOtpOpen = false;
      this.phoneEditing = false;
    },
    phoneVerificationCancelled() {
      this.$set(this.optionsLocal, "phone", this.phoneAuthoritative);
      this.phoneOtpOpen = false;
      this.phoneEditing = false;
    },
    phoneBannerAction() {
      // Unverified valid phone: straight into the change/verify flow. Phoneless
      // (or invalid legacy value): open the gated input for editing first.
      if (this.optionsLocal.phone && this.phoneInputValid) {
        this.openPhoneVerification();
        return;
      }
      this.startPhoneEdit();
    },
    /* eslint-enable */
    // Gated-field lifecycle: the input is disabled until this runs; the
    // enable + autofocus transition is the editability signal.
    startPhoneEdit() {
      if (this.phoneEditing) return
      if (this.phoneAuthoritative === undefined) this.phoneAuthoritative = this.optionsLocal.phone || null
      this.phoneEditing = true
      this.focusAccountInput('phone')
    },
    cancelPhoneEdit() {
      this.$set(this.optionsLocal, 'phone', this.phoneAuthoritative)
      this.phoneEditing = false
    },
    focusAccountInput(name) {
      // The input only becomes focusable after the DOM applies the enabled
      // state, hence the tick. Guarded: unit specs invoke methods against a
      // crafted `this` with no mounted element.
      this.$nextTick(() => {
        const input = this.$el && this.$el.querySelector(`input[name="${name}"]`)
        if (input) input.focus()
      })
    },
    codeFormatter(value) {
      return String(value == null ? '' : value).replace(/\D/g, '').slice(0, 6)
    },
    emailBannerAction() {
      // WP1: an already-stored, syntactically valid but unverified email goes
      // straight into the new no-password confirm flow (this banner exists
      // precisely for that address, so there is nothing to type or change).
      // Anything else (blank or invalid) sends the user to the input to
      // type/fix one first -- unchanged from before.
      if (this.optionsLocal.email && this.emailInputValid) {
        this.openEmailConfirm()
        return
      }
      // Blank or invalid stored email: the gated input must be opened for
      // editing before there is anything to focus or type into.
      this.startEmailEdit()
    },
    // Gated-field lifecycle, mirroring startPhoneEdit: enable + autofocus is
    // the editability signal. Deliberately does NOT open the password panel —
    // the banner lane lands here so the user can type an address first.
    startEmailEdit() {
      if (this.emailEditing) return
      if (this.emailAuthoritative === undefined) this.emailAuthoritative = this.optionsLocal.email || null
      this.emailEditing = true
      this.focusAccountInput('email')
    },
    openEmailVerification() {
      // Entry validity guard removed with the gated fields: the button must be
      // pressable while the input still shows the (possibly blank) stored
      // value. Validity is enforced where it matters — submitEmailPassword and
      // the panel's send button both gate on emailInputValid.
      this.startEmailEdit()
      this.emailChangeOpen = true
      this.emailChangeStep = 'password'
      this.emailChangeError = ''
    },
    cancelEmailChange() {
      this.$set(this.optionsLocal, 'email', this.emailAuthoritative)
      this.emailEditing = false
      this.emailChangeOpen = false
      this.emailChangeStep = 'password'
      this.emailChangePassword = ''
      this.emailChangeCode = ''
      this.emailChangeChallengeId = ''
      this.emailChangeError = ''
    },
    submitEmailPassword() {
      if (this.emailChangeLoading || !this.emailChangePassword || !this.emailInputValid) return
      this.emailChangeLoading = true
      this.emailChangeError = ''
      axios
        .post(authRoutes.emailChangeRequest, {
          password: this.emailChangePassword,
          email: this.optionsLocal.email,
        }, { suppressErrorToast: true })
        .then(({ data }) => {
          this.emailChangeChallengeId = data.challengeId
          this.emailChangeCode = data.devCode || ''
          this.emailChangeStep = 'code'
        })
        .catch(error => {
          const data = (error.response && error.response.data) || {}
          const mapped = OTP_ERROR_KEYS_BY_CODE[Number(data.errorCode)]
          this.emailChangeError = mapped ? this.$t(mapped) : (data.message || this.$t(OTP_ERROR_KEYS.invalidCode))
        })
        .finally(() => {
          this.emailChangeLoading = false
        })
    },
    verifyEmailChange() {
      if (this.emailChangeLoading || !/^\d{6}$/.test(this.emailChangeCode)) return
      this.emailChangeLoading = true
      this.emailChangeError = ''
      axios
        .post(authRoutes.emailChangeVerify, {
          challengeId: this.emailChangeChallengeId,
          code: this.emailChangeCode,
        }, { suppressErrorToast: true })
        .then(({ data }) => {
          this.$set(this.optionsLocal, 'email', data.email)
          this.$set(this.optionsLocal, 'emailApproved', true)
          this.emailAuthoritative = data.email
          this.emailEditing = false
          this.emailChangeOpen = false
          this.emailChangeStep = 'password'
          this.emailChangePassword = ''
          this.emailChangeCode = ''
          this.emailChangeChallengeId = ''
        })
        .catch(error => {
          const data = (error.response && error.response.data) || {}
          const mapped = OTP_ERROR_KEYS_BY_CODE[Number(data.errorCode)]
          this.emailChangeError = mapped ? this.$t(mapped) : (data.message || this.$t(OTP_ERROR_KEYS.invalidCode))
        })
        .finally(() => {
          this.emailChangeLoading = false
        })
    },
    // WP1: confirm flow -- no password, no typed address; the server always
    // targets the account's own stored, unverified email. Distinct from the
    // submitEmailPassword/verifyEmailChange pair above, which stay unchanged.
    // WP2: read-only probe for a challenge that is ALREADY live -- typically
    // the one the registration mail delivered. It mints nothing, is exempt
    // from the OTP rate limiter and never touches attemptCount, so it is safe
    // to fire on page load. Without it the only way to reveal the code input
    // was to request a new code, which superseded the very code the user was
    // copying out of their inbox (observed in production: code mailed at
    // 19:00:32, panel opened 19:02:13, verify at 19:02:17 -> mismatch).
    lookupActiveEmailConfirm() {
      if (this.emailConfirmLookupDone) return
      const account = this.optionsLocal
      if (!account || !account.email || account.emailApproved) return
      this.emailConfirmLookupDone = true
      axios
        .get(authRoutes.emailConfirmActive, { suppressErrorToast: true })
        .then(({ data }) => {
          if (this.emailConfirmTorndown) return
          if (!data || !data.active || !data.challengeId) {
            // No live code: open in send mode so the user reaches an explicit
            // action rather than an input with nothing to type into it.
            this.emailConfirmOpen = true
            return
          }
          this.emailConfirmChallengeId = data.challengeId
          this.emailConfirmCode = ''
          this.emailConfirmError = ''
          this.emailConfirmOpen = true
          this.startEmailConfirmExpiry(data.expiresInSeconds)
        })
        .catch(() => {
          // A failed probe must never strand the user: the explicit send
          // action stays reachable, it just is not pre-empted by an input.
          if (this.emailConfirmTorndown) return
          this.emailConfirmOpen = true
        })
    },
    startEmailConfirmExpiry(seconds) {
      this.stopEmailConfirmExpiry()
      if (this.emailConfirmTorndown) return
      this.emailConfirmExpiresIn = Number(seconds) || 0
      this.emailConfirmExpired = this.emailConfirmExpiresIn <= 0
      if (this.emailConfirmExpired) return
      this.emailConfirmExpiryTimer = setInterval(() => {
        if (this.emailConfirmExpiresIn > 0) this.emailConfirmExpiresIn -= 1
        if (this.emailConfirmExpiresIn <= 0) {
          this.emailConfirmExpired = true
          this.stopEmailConfirmExpiry()
        }
      }, 1000)
    },
    stopEmailConfirmExpiry() {
      if (this.emailConfirmExpiryTimer) clearInterval(this.emailConfirmExpiryTimer)
      this.emailConfirmExpiryTimer = null
    },
    openEmailConfirm() {
      if (!this.optionsLocal.email || this.optionsLocal.emailApproved) return
      // WP2: opening is now inert. Whether a code input or a send button is
      // shown follows from emailConfirmChallengeId, which only the read-only
      // lookup or an explicit send ever populates.
      this.emailConfirmOpen = true
      this.emailConfirmError = ''
    },
    requestEmailConfirmCode() {
      if (this.emailConfirmRequesting) return
      this.emailConfirmRequesting = true
      this.emailConfirmError = ''
      axios
        .post(authRoutes.emailConfirmRequest, {}, { suppressErrorToast: true })
        .then(({ data }) => {
          this.emailConfirmChallengeId = data.challengeId
          this.emailConfirmCode = data.devCode || ''
          this.startEmailConfirmExpiry(data.expiresInSeconds)
        })
        .catch(error => {
          const data = (error.response && error.response.data) || {}
          const mapped = OTP_ERROR_KEYS_BY_CODE[Number(data.errorCode)]
          this.emailConfirmError = mapped ? this.$t(mapped) : (data.message || this.$t(OTP_ERROR_KEYS.invalidCode))
        })
        .finally(() => {
          this.emailConfirmRequesting = false
        })
    },
    verifyEmailConfirm() {
      if (this.emailConfirmLoading || !/^\d{6}$/.test(this.emailConfirmCode)) return
      this.emailConfirmLoading = true
      this.emailConfirmError = ''
      axios
        // Verification is purpose/channel/owner-scoped, not tied to which
        // request route created the challenge, so this reuses the same
        // verify-otp endpoint the password-gated change flow uses.
        .post(authRoutes.emailChangeVerify, {
          challengeId: this.emailConfirmChallengeId,
          code: this.emailConfirmCode,
        }, { suppressErrorToast: true })
        .then(({ data }) => {
          this.$set(this.optionsLocal, 'email', data.email)
          this.$set(this.optionsLocal, 'emailApproved', true)
          this.emailAuthoritative = data.email
          this.cancelEmailConfirm()
        })
        .catch(error => {
          const data = (error.response && error.response.data) || {}
          const mapped = OTP_ERROR_KEYS_BY_CODE[Number(data.errorCode)]
          this.emailConfirmError = mapped ? this.$t(mapped) : (data.message || this.$t(OTP_ERROR_KEYS.invalidCode))
        })
        .finally(() => {
          this.emailConfirmLoading = false
        })
    },
    cancelEmailConfirm() {
      this.emailConfirmOpen = false
      this.emailConfirmCode = ''
      this.emailConfirmChallengeId = ''
      this.emailConfirmError = ''
      this.stopEmailConfirmExpiry()
      this.emailConfirmExpiresIn = 0
      this.emailConfirmExpired = false
    },
    nullFunc() {},
    emailFormatter(email) {
      // Strip anything outside the strict ASCII e-mail alphabet as the user
      // types (the incident "ç" never even lands in the field). Unlike the old
      // formatter this keeps the legal specials _ % + - .
      return email.replace(/[^A-Za-z0-9@._%+-]/g, '')
    },
    emailValidate(event) {
      const { target } = event
      const email = target.value
      if (!email) {
        target.classList.remove('is-invalid')
        target.placeholder = this.$t('E-mail')
        return
      }
      if (!isValidEmailStrict(email)) {
        target.placeholder = this.$t('Invalid Email')
        target.classList.add('is-invalid')
      }
    },
    fileinputclick() {
      // #fileinput click
      document.getElementById('fileinput').click()
    },
    shareReferralCode() {
      this.$refs.sharePlatformsModal.show()
      // if (navigator.share) {
      //   navigator
      //     .share({
      //       title: "Diko",
      //       text: "Diko Referral Code",
      //       url: this.optionsLocal._id,
      //     })
      //     .then(() => console.log("Successful share"))
      //     .catch((error) => console.log("Error sharing", error));
      // } else {
      //   console.log("Share not supported");
      // }
    },
    copyReferralCode() {
      const { _id: referralId } = this.optionsLocal
      const el = document.createElement('textarea')
      el.value = referralId
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
    },
    resetImage() {
      console.log('resetImage')
      // console.log(this.profileFile);
      this.profileFile = null
      // console.log(this.profileFile);
      this.$refs.previewEl.src = `${axios.defaults.baseURL}/images/dikologo.png`
      this.avatar = `${axios.defaults.baseURL}/images/dikologo.png`
      this.resetUserImage = true
    },
    popupAccountApproval() {
      // this.$bvModal.show("modal-approve-account");
    },
    obscureID(id) {
      if (!id) return ''
      if (id.length > 4) return id.replace(/(?!(^..|.?$)).(?=.{2})/g, '*')
      return id
    },
    identificationFormatter(value) {
      // only numbers minimum 10 maximum 11 digits
      return value.replace(/[^\d]/g, '').slice(0, 11)
    },
    showInitialApprovalSuccess() {
      this.$swal({
        text: this.approvalSuccessText('approvalLifecycle.submitted.successBody'),
        icon: 'success',
        confirmButtonText: this.$t('approvalLifecycle.submitted.successCta'),
      })
    },
    approvalSuccessText(key) {
      const free = this.optionsLocal && this.optionsLocal.freeAccess
      return [this.$t(key), free && free.eligible === true
        ? this.$t('approvalLifecycle.submitted.freeAccessHint') : ''].filter(Boolean).join(' ')
    },
    showResubmissionSuccess() {
      this.$swal({
        title: this.$t('approvalLifecycle.submitted.title'),
        text: this.approvalSuccessText('approvalLifecycle.rejected.resubmitted'),
        icon: 'success',
        confirmButtonText: this.$t('Ok'),
      })
    },
    async updateProfile() {
      if (!this.optionsLocal || this.profileSaving || this.profileSaveNeedsCheck
        || this.optionsLocal.awaitingApproval
        || (this.optionsLocal.approved && this.isDirty === false)) return
      const validationError = profileValidationError(this.optionsLocal, this.degreeFile, this.profileFile)
      if (validationError) {
        this.profileSaveError = validationError
        return
      }
      this.profileSaving = true
      this.profileSaveError = ''
      this.profileSaveNotice = ''
      this.profileRecoveryNotice = ''
      this.profileRefreshWarning = false
      const { _id: userId } = this.optionsLocal
      const wasRejected = resolveApprovalLifecycle(this.optionsLocal).approvalStatus === 'rejected'
      const submitsForReview = !this.optionsLocal.approved
      let stage = 'prepare-upload'
      let saved
      try {
        // Use the selected File models, not BootstrapVue's internal ref.files.
        // Optional files must be omitted rather than sent as the string "undefined".
        const formData = new FormData()
        if (this.profileFile) formData.append('avatarfile', this.profileFile)
        if (submitsForReview && this.degreeFile) formData.append('degreeFile', this.degreeFile)
        formData.append('identification', this.optionsLocal.identificationNumber || '')
        formData.append('referrer', this.optionsLocal.referrer || '')
        formData.append('name', this.optionsLocal.name.trim())
        formData.append('surname', this.optionsLocal.surname.trim())
        formData.append('resetUserImage', this.resetUserImage)
        stage = 'upload'
        const response = await axios.patch(`${path.edit}/${userId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: PROFILE_UPLOAD_TIMEOUT_MS,
          suppressErrorToast: true,
        })
        saved = response.data
        const { _id: savedAccountId } = saved || {}
        if (!saved || savedAccountId !== userId || typeof saved.approved !== 'boolean') {
          throw new Error('Unexpected save response')
        }
      } catch (error) {
        logProfileSaveFailure(stage, error)
        this.profileSaveError = stage === 'prepare-upload'
          ? 'accountProfileSave.prepareFailed' : profileSaveErrorKey(error)
        // A timeout/connection loss can happen AFTER the server commits.
        // Require a read-only status check before another application upload.
        this.profileSaveNeedsCheck = submitsForReview && stage === 'upload'
          && profileSaveOutcomeUncertain(error)
        return
      } finally {
        this.profileSaving = false
      }
      if (this.profileSaveDisposed) return
      this.finishProfileSave(saved, submitsForReview, wasRejected)
      // The PATCH acknowledgement is sufficient for success. Refreshing the
      // lifecycle is separate and cannot turn a saved application into a failure.
      this.refreshSavedProfile()
    },
    finishProfileSave(data, submitsForReview, wasRejected) {
      const awaitingApproval = submitsForReview && !data.approved
      this.optionsLocal = {
        ...this.optionsLocal,
        ...data,
        avatar: axios.defaults.baseURL + (data.avatar || '/images/dikologo.png'),
        identificationNumber: data.identification || '',
        ...(awaitingApproval ? {
          awaitingApproval: true,
          rejectedApproval: false,
          approval: { ...data.approval, status: 'pending', reviewComment: null },
          // Do not let the previous not-submitted/rejected standing override
          // the pending application acknowledged by the save endpoint.
          approvalStanding: null,
        } : {}),
        submissionJustConfirmed: awaitingApproval && !wasRejected,
        resubmissionJustConfirmed: awaitingApproval && wasRejected,
      }
      this.initialData = JSON.stringify({
        name: this.optionsLocal.name,
        surname: this.optionsLocal.surname,
        referrer: this.optionsLocal.referrer,
        identificationNumber: this.optionsLocal.identificationNumber,
      })
      this.degreeFile = null
      this.profileFile = null
      this.resetUserImage = false
      this.profileSaveNeedsCheck = false
      this.profileSaveError = ''
      this.profileRecoveryNotice = ''
      this.profileSaveNotice = awaitingApproval
        ? '' : 'Changes Saved Successfully'
      invalidateSubscriptionStanding()
      try {
        localStorage.setItem('userAvatar', this.optionsLocal.avatar)
      } catch (error) {
        logProfileSaveFailure('avatar-cache', error)
      }
      if (awaitingApproval) {
        if (wasRejected) this.showResubmissionSuccess()
        else this.showInitialApprovalSuccess()
      }
    },
    async refreshSavedProfile() {
      const savedAccount = this.optionsLocal
      try {
        const { data } = await axios.get(path.my, {
          timeout: PROFILE_STATUS_TIMEOUT_MS,
          suppressErrorToast: true,
        })
        if (this.profileSaveDisposed || this.optionsLocal !== savedAccount) return
        // Only refresh server-owned status fields; a late GET must not erase
        // edits or newly selected files made after an approved profile was saved.
        this.optionsLocal = mergeProfileStatus(this.optionsLocal, data)
      } catch (error) {
        if (this.profileSaveDisposed || this.optionsLocal !== savedAccount) return
        logProfileSaveFailure('refresh-profile', error)
        this.profileRefreshWarning = true
      }
    },
    async checkProfileSubmission() {
      if (this.profileSaving || !this.profileSaveNeedsCheck) return
      this.profileSaving = true
      const checkedAccount = this.optionsLocal
      try {
        const { data } = await axios.get(path.my, {
          timeout: PROFILE_STATUS_TIMEOUT_MS,
          suppressErrorToast: true,
        })
        if (this.profileSaveDisposed || this.optionsLocal !== checkedAccount) return
        const { _id: currentAccountId } = this.optionsLocal
        const { _id: responseAccountId } = data || {}
        if (!data || responseAccountId !== currentAccountId || typeof data.approved !== 'boolean'
          || !data.approval || !['not-submitted', 'pending', 'approved', 'rejected'].includes(data.approval.status)
          || data.approved !== (data.approval.status === 'approved')
          || (data.approvalStanding && data.approvalStanding.approvalStatus !== data.approval.status)) {
          throw new Error('Unexpected account response')
        }
        const { status } = data.approval
        // An approval row does not acknowledge the entire upload. Keep the
        // draft, files, and saved baseline until a PATCH is acknowledged.
        this.optionsLocal = mergeProfileStatus(this.optionsLocal, {
          ...data,
          awaitingApproval: status === 'pending',
          rejectedApproval: status === 'rejected',
        })
        this.profileSaveNeedsCheck = false
        this.profileSaveError = data.accountBlocked && !data.approved
          ? 'accountProfileSave.blocked' : ''
        this.profileRecoveryNotice = {
          pending: 'accountProfileSave.pendingStatus',
          approved: 'accountProfileSave.approvedStatus',
          rejected: 'accountProfileSave.rejectedStatus',
          'not-submitted': 'accountProfileSave.notSubmitted',
        }[status]
        invalidateSubscriptionStanding()
      } catch (error) {
        if (this.profileSaveDisposed || this.optionsLocal !== checkedAccount) return
        logProfileSaveFailure('check-submission', error)
        this.profileSaveError = 'accountProfileSave.statusUnavailable'
      } finally {
        this.profileSaving = false
      }
    },
  },
  // mounted() {
  //   setInterval(() => {
  //     console.log(
  //       this.optionsLocal.phone &&
  //         this.optionsLocal.identificationNumber &&
  //         this.optionsLocal.name &&
  //         this.optionsLocal.email &&
  //         this.degreeFile &&
  //         !this.awaitingApproval,
  //       this.optionsLocal.phone,
  //       this.optionsLocal.identificationNumber,
  //       this.optionsLocal.name,
  //       this.optionsLocal.email,
  //       this.degreeFile,
  //       !this.awaitingApproval
  //     );
  //   }, 1000);
  // },
  setup(props, refs) {
    // console.log("refs", refs);
    // console.log("props", props);
    const refInputEl = ref(null)
    const previewEl = ref(null)
    const fileItself = ref(null)
    const optionsLocal = ref(null)
    const initialData = ref(null)
    const { refs: componentRefs } = refs
    // let userData = {};
    const previewElChange = image => {
      // refs.previewEl.src = image;
      // props.generalData.avatar = image;
      componentRefs.previewEl.src = image
      // console.log(previewEl);
    }
    const { inputImageRenderer } = useInputImageRenderer(refInputEl, previewElChange)
    axios.get(path.my).then(({ data }) => {
      // console.log(res.data);
      // refs.refs.optionsLocal = res.data;
      // userData = data;
      // this.userData.avatar = axios.defaults.baseURL + data.avatar;
      optionsLocal.value = data
      optionsLocal.value.avatar = axios.defaults.baseURL + data.avatar
      optionsLocal.value.identificationNumber = data.identification
      if (data.referrer) {
        optionsLocal.value.initialReferrer = data.referrer
        optionsLocal.value.referrerName = data.referrerName
        if (data.referrerDiscount) optionsLocal.value.referrerDiscount = data.referrerDiscount
        else optionsLocal.value.referrerDiscount = 0
      }
      // snapshot of the loaded values for dirty-state tracking
      initialData.value = JSON.stringify({
        name: optionsLocal.value.name,
        surname: optionsLocal.value.surname,
        referrer: optionsLocal.value.referrer,
        identificationNumber: optionsLocal.value.identificationNumber,
      })

      const loadedAccount = optionsLocal.value
      fetchSubscriptionStanding()
        .then(standing => {
          if (optionsLocal.value === loadedAccount) {
            optionsLocal.value = mergeApprovalLifecycleAccount(optionsLocal.value, standing)
          }
        })
        .catch(() => {})

      // optionsLocal.value.referrer = data.referrer._id;
    })
    const { baseURL } = axios.defaults
    return {
      refInputEl,
      previewEl,
      inputImageRenderer,
      fileItself,
      optionsLocal,
      initialData,
      baseURL,
      // userData,
    }
  },
}
</script>
<style lang="scss" scoped>
@import '~@core/scss/base/bootstrap-extended/include';

.is-invalid {
  border-color: $danger;
}

.no-cursor:hover {
  cursor: default !important;
  box-shadow: none !important;
}

.completion-field {
  ::v-deep label,
  ::v-deep .form-control,
  ::v-deep .custom-file-label {
    transition: border-color 0.15s ease-in-out, color 0.15s ease-in-out,
      box-shadow 0.15s ease-in-out;
  }
}

.completion-field--incomplete {
  ::v-deep label {
    color: $primary;
  }

  ::v-deep .form-control:not(.is-invalid),
  ::v-deep .custom-file-label {
    border-color: $primary;
  }

  ::v-deep .form-control:not(.is-invalid):focus,
  ::v-deep .custom-file-input:focus ~ .custom-file-label {
    border-color: $primary;
    box-shadow: 0 0 0 0.2rem rgba($primary, 0.2);
  }
}

.completion-field--complete {
  ::v-deep label {
    color: $text-muted;
  }

  ::v-deep .form-control,
  ::v-deep .custom-file-label {
    border-color: $input-border-color;
  }

  ::v-deep .form-control:focus,
  ::v-deep .custom-file-input:focus ~ .custom-file-label {
    border-color: $text-muted;
    box-shadow: 0 0 0 0.2rem rgba($text-muted, 0.2);
  }
}

.completion-field--incomplete ::v-deep .form-control.is-invalid,
.completion-field--complete ::v-deep .form-control.is-invalid {
  border-color: $danger;
  box-shadow: none;
}
</style>
